// Luxury vitrine — the signature Aarya24KT product display panel.
// - Use `mode="clean"` for product photos shot on plain white backgrounds (server-side bg removal + warm coal panel + gold spotlight)
// - Use `mode="bleed"` for moody lifestyle / dark-bg photos (full-bleed image, gold hairline frame and corner accents preserved)
// - Use `mode="framed"` for designed/framed artworks (image shown on a soft ivory mat to honour the original framing)
import { Reveal } from "@/components/Reveal";
import { IS_EXPORT } from "@/lib/exportMode";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const cleanUrl = (src, threshold = 232, soft = 26) =>
  `${BACKEND_URL}/api/asset/clean-bg?url=${encodeURIComponent(src)}&threshold=${threshold}&soft=${soft}`;

export default function Vitrine({
  src,
  alt = "",
  mode = "clean",
  topLabel,
  bottomLabel,
  aspect = "aspect-[3/4]",
  delay = 0,
  cleanOptions,
  priority = false,
}) {
  // Always eager-load during PDF export so every image renders.
  const imgLoadProps = (priority || IS_EXPORT)
    ? { loading: "eager", fetchpriority: "high" }
    : { loading: "lazy", fetchpriority: "low", decoding: "async" };
  const renderInner = () => {
    if (mode === "bleed") {
      return (
        <>
          {/* Deep warm coal backdrop so contained lifestyle shots feel unified with clean-mode panels */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 70% 60% at 50% 38%, #2A2218 0%, #1A140E 45%, #0E0A06 100%)",
            }}
          />
          <img
            src={src}
            alt={alt}
            crossOrigin="anonymous"
            {...imgLoadProps}
            className="absolute inset-0 h-full w-full object-contain p-2 md:p-3 lg:p-4 transition-transform duration-[1400ms] ease-luxe hover:scale-[1.04]"
            style={{
              filter:
                "drop-shadow(0 30px 60px rgba(200,164,106,0.18)) drop-shadow(0 12px 24px rgba(0,0,0,0.6))",
            }}
          />
        </>
      );
    }
    if (mode === "framed") {
      return (
        <>
          {/* Soft ivory mat to honour the framed-artwork aesthetic */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 70% 60% at 50% 45%, #E8DFCB 0%, #D6C9A8 60%, #B59E76 100%)",
            }}
          />
          <img
            src={src}
            alt={alt}
            crossOrigin="anonymous"
            {...imgLoadProps}
            className="absolute inset-0 h-full w-full object-contain p-4 md:p-5 transition-transform duration-[1400ms] ease-luxe hover:scale-[1.04]"
          />
        </>
      );
    }
    // Default: "clean" mode — backend removes the white background, product on warm coal vitrine
    const url = cleanUrl(src, cleanOptions?.threshold, cleanOptions?.soft);
    return (
      <>
        {/* Deep warm coal */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 60% at 50% 38%, #2A2218 0%, #1A140E 45%, #0E0A06 100%)",
          }}
        />
        {/* Warm gold haze spotlight */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 55% 40% at 50% 25%, rgba(200,164,106,0.28) 0%, rgba(200,164,106,0.06) 45%, transparent 75%)",
          }}
        />
        {/* Subtle floor reflection */}
        <div
          className="absolute inset-x-0 bottom-0 h-1/3"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(200,164,106,0.08) 50%, rgba(200,164,106,0.16) 100%)",
          }}
        />
        <img
          src={url}
          alt={alt}
          crossOrigin="anonymous"
          {...imgLoadProps}
          className="absolute inset-0 h-full w-full object-contain p-2 md:p-3 lg:p-4 transition-transform duration-[1400ms] ease-luxe hover:scale-[1.04]"
          style={{
            filter:
              "drop-shadow(0 36px 64px rgba(200,164,106,0.22)) drop-shadow(0 14px 28px rgba(0,0,0,0.7))",
          }}
        />
      </>
    );
  };

  return (
    <Reveal delay={delay} className="w-full">
      <div className={`relative ${aspect} w-full overflow-hidden`}>
        {renderInner()}
        {/* Gold hairline frame */}
        <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-gold/30" />
        {/* Corner accents */}
        <div className="pointer-events-none absolute top-3 left-3 h-5 w-5 border-t border-l border-gold/60" />
        <div className="pointer-events-none absolute top-3 right-3 h-5 w-5 border-t border-r border-gold/60" />
        <div className="pointer-events-none absolute bottom-3 left-3 h-5 w-5 border-b border-l border-gold/60" />
        <div className="pointer-events-none absolute bottom-3 right-3 h-5 w-5 border-b border-r border-gold/60" />
        {/* Editorial captions */}
        {topLabel && (
          <div className="absolute top-5 right-5 num-overline text-gold/75 z-10">
            {topLabel}
          </div>
        )}
        {bottomLabel && (
          <div className="absolute bottom-5 left-5 num-overline text-gold/80 z-10">
            {bottomLabel}
          </div>
        )}
      </div>
    </Reveal>
  );
}
